/**
 * useGamification — hook de gamification dual mode :
 *  - Si l'utilisateur N'EST PAS connecté  → state stocké en localStorage (mode anonyme)
 *  - Si l'utilisateur EST connecté        → state synchronisé avec MongoDB via /api/progress/gamification
 *
 * Au moment du login, l'état local (anonyme) est automatiquement
 * migré vers le compte (max sur xp/streak, fusion trophées),
 * puis effacé du localStorage.
 *
 * Garde la même API publique que l'ancien `useGamificationLocal` pour ne pas
 * casser les composants existants :
 *   const g = useGamification();
 *   g.xp, g.streak, g.streakState, g.level, g.levelName, g.nextLevelName,
 *   g.xpCurrent, g.xpForNext, g.trophies,
 *   g.addXp(n), g.pingStreak(), g.unlockTrophy(t), g.reset()
 */
import { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { useApp } from '../contexts/AppContext';

const STORAGE_KEY = 'c2r_gamification_v1';
const MIGRATED_FLAG = 'c2r_gamif_migrated_v1';

const DEFAULT_STATE = {
  xp: 0,
  streak: 0,
  lastActivityDate: null,
  trophies: [],
};

// Hiérarchie de niveaux 100% francophone (refonte mai 2026).
// Cible élèves jeunes (ASSR, BSR, A1) + adultes : tous les termes doivent
// être immédiatement compréhensibles, sans anglicisme.
const LEVEL_THRESHOLDS = [
  { level: 1, name: 'Apprenti', xpRequired: 0 },
  { level: 2, name: 'Pilote', xpRequired: 100 },
  { level: 3, name: 'Confirmé', xpRequired: 300 },
  { level: 4, name: 'Expert', xpRequired: 700 },
  { level: 5, name: 'Légende', xpRequired: 1500 },
];

const getLevelInfo = (xp) => {
  let i = 0;
  for (let k = LEVEL_THRESHOLDS.length - 1; k >= 0; k--) {
    if (xp >= LEVEL_THRESHOLDS[k].xpRequired) { i = k; break; }
  }
  const cur = LEVEL_THRESHOLDS[i];
  const nxt = LEVEL_THRESHOLDS[i + 1] || null;
  return {
    level: cur.level,
    levelName: cur.name,
    nextLevelName: nxt ? nxt.name : 'MAX',
    xpCurrent: xp - cur.xpRequired,
    xpForNext: nxt ? nxt.xpRequired - cur.xpRequired : 0,
    xpTotal: xp,
    isMax: !nxt,
  };
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const daysBetween = (d1, d2) => {
  if (!d1 || !d2) return Infinity;
  const a = new Date(d1 + 'T00:00:00');
  const b = new Date(d2 + 'T00:00:00');
  return Math.round((b - a) / 86400000);
};

const computeStreakState = (streak, lastActivityDate) => {
  if (!streak || streak === 0) return 'broken';
  const today = todayIso();
  if (lastActivityDate === today) return 'active';
  if (daysBetween(lastActivityDate, today) === 1) return 'at-risk';
  return 'broken';
};

// ──────────────── localStorage helpers (mode anonyme) ────────────────
const readLocal = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_STATE };
    return { ...DEFAULT_STATE, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_STATE };
  }
};

const writeLocal = (state) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* quota */ }
};

const clearLocal = () => {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
};

// ──────────────── Convertit la réponse backend → format hook ────────────────
const fromServer = (s) => ({
  xp: Number(s?.xp || 0),
  streak: Number(s?.streak || 0),
  lastActivityDate: s?.last_activity_date || null,
  trophies: Array.isArray(s?.trophies)
    ? s.trophies.map((t) => ({
        id: t.id,
        name: t.name,
        color: t.color,
        icon: t.icon,
        unlockedAt: t.unlocked_at || t.unlockedAt,
      }))
    : [],
});

// ──────────────── Hook principal ────────────────
export const useGamification = () => {
  const { user } = useApp();
  const [state, setState] = useState(() =>
    typeof window !== 'undefined' ? readLocal() : DEFAULT_STATE
  );
  const [synced, setSynced] = useState(false);

  // Sync cross-tab pour le mode anonyme
  useEffect(() => {
    if (user) return undefined;
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setState(readLocal());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [user]);

  // Lorsqu'on bascule en mode connecté : on migre + on fetch
  useEffect(() => {
    if (!user) {
      // user → null (logout ou non connecté) : on relit le state local
      setSynced(false);
      setState(readLocal());
      return undefined;
    }

    let cancelled = false;
    const sync = async () => {
      try {
        // 1) Si on a un état local non migré, on tente la migration one-shot
        const local = readLocal();
        const hasLocalProgress =
          local && (local.xp > 0 || local.streak > 0 || (local.trophies || []).length > 0);
        const alreadyMigrated = localStorage.getItem(MIGRATED_FLAG) === '1';

        let serverState;
        if (hasLocalProgress && !alreadyMigrated) {
          serverState = await api.migrateGamification(local);
          localStorage.setItem(MIGRATED_FLAG, '1');
          clearLocal();
        } else {
          serverState = await api.getGamification();
          // Marque comme migré pour éviter d'autres tentatives
          if (!alreadyMigrated) localStorage.setItem(MIGRATED_FLAG, '1');
        }

        if (!cancelled) {
          setState(fromServer(serverState));
          setSynced(true);
        }
      } catch (e) {
        // Silencieux — on garde le state local comme fallback
        if (!cancelled) setSynced(false);
      }
    };
    sync();
    return () => { cancelled = true; };
  }, [user]);

  // ─── Mutations ─────────────────────────────────────────────────────
  const persistAnonymous = useCallback((next) => {
    writeLocal(next);
  }, []);

  const addXp = useCallback(
    async (n) => {
      if (!n || n <= 0) return;
      // Optimistic update (commun aux 2 modes)
      setState((prev) => {
        const next = { ...prev, xp: (prev.xp || 0) + n };
        if (!user) persistAnonymous(next);
        return next;
      });
      if (user) {
        try {
          const s = await api.addGamificationXp(n);
          setState(fromServer(s));
        } catch { /* on conserve l'optimistic update */ }
      }
    },
    [user, persistAnonymous]
  );

  const pingStreak = useCallback(async () => {
    const today = todayIso();
    setState((prev) => {
      if (prev.lastActivityDate === today) return prev;
      const gap = daysBetween(prev.lastActivityDate, today);
      const newStreak = gap === 1 ? (prev.streak || 0) + 1 : 1;
      const next = { ...prev, streak: newStreak, lastActivityDate: today };
      if (!user) persistAnonymous(next);
      return next;
    });
    if (user) {
      try {
        const s = await api.pingGamificationStreak();
        setState(fromServer(s));
      } catch { /* ignore */ }
    }
  }, [user, persistAnonymous]);

  const unlockTrophy = useCallback(
    async (trophy) => {
      if (!trophy || !trophy.id) return;
      // Sanitize : on ne garde que des champs string sérialisables.
      // `trophy` peut contenir des références à des composants React (icon),
      // des thresholds numériques, etc. → on extrait proprement.
      const safeTrophy = {
        id: String(trophy.id),
        name:
          (typeof trophy.name === 'string' && trophy.name) ||
          (typeof trophy.title === 'string' && trophy.title) ||
          (typeof trophy.stamp === 'string' && trophy.stamp) ||
          String(trophy.id),
        color: typeof trophy.color === 'string' ? trophy.color : '#cf2031',
        icon: typeof trophy.icon === 'string' ? trophy.icon : 'trophy',
      };
      setState((prev) => {
        if ((prev.trophies || []).find((t) => t.id === safeTrophy.id)) return prev;
        const next = {
          ...prev,
          trophies: [
            ...(prev.trophies || []),
            { ...safeTrophy, unlockedAt: new Date().toISOString() },
          ],
        };
        if (!user) persistAnonymous(next);
        return next;
      });
      if (user) {
        try {
          const s = await api.unlockGamificationTrophy(safeTrophy);
          setState(fromServer(s));
        } catch { /* ignore */ }
      }
    },
    [user, persistAnonymous]
  );

  const reset = useCallback(async () => {
    setState({ ...DEFAULT_STATE });
    if (user) {
      try {
        const s = await api.resetGamification();
        setState(fromServer(s));
      } catch { /* ignore */ }
    } else {
      writeLocal(DEFAULT_STATE);
    }
  }, [user]);

  // ─── Dérivés ───────────────────────────────────────────────────────
  const levelInfo = getLevelInfo(state.xp || 0);
  const streakState = computeStreakState(state.streak, state.lastActivityDate);

  return {
    xp: state.xp || 0,
    streak: state.streak || 0,
    streakState,
    lastActivityDate: state.lastActivityDate,
    trophies: state.trophies || [],
    ...levelInfo,
    addXp,
    pingStreak,
    unlockTrophy,
    reset,
    isRemote: !!user,
    isSynced: synced,
  };
};

export default useGamification;
